import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { PageHeader, StatusBadge } from "@/components/app/PageBits";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Search, Wallet, Paperclip } from "lucide-react";
import { fmtINR, fmtDate } from "@/lib/api";
import { QuickVendorDialog } from "@/components/app/QuickVendorDialog";

const EMPTY = { project_id: "", vendor_id: "", category_id: "", bill_date: "", due_date: "", amount: "", gst_amount: "", bill_no: "", notes: "" };
const PAY_EMPTY = { amount: "", method: "bank_transfer", reference_no: "", paid_date: "", notes: "" };

export default function Expenses() {
  const [searchParams] = useSearchParams();
  const [list, setList] = useState([]);
  const [projects, setProjects] = useState([]);
  const [categories, setCategories] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [qvOpen, setQvOpen] = useState(false);
  const [q, setQ] = useState(searchParams.get("q") || "");
  const [status, setStatus] = useState(searchParams.get("status") || "all");
  const [projectFilter, setProjectFilter] = useState(searchParams.get("project_id") || "all");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [payOpen, setPayOpen] = useState(false);
  const [payFor, setPayFor] = useState(null);
  const [pay, setPay] = useState(PAY_EMPTY);
  const [filePick, setFilePick] = useState(null);

  const load = async () => {
    const params = {};
    if (q.trim()) params.q = q.trim();
    if (status !== "all") params.status = status;
    if (projectFilter !== "all") params.project_id = projectFilter;
    const [e, p, c] = await Promise.all([
      api.get("/expenses", { params }), api.get("/projects"), api.get("/expense-categories"),
    ]);
    setList(e.data); setProjects(p.data); setCategories(c.data);
  };
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [q, status, projectFilter]);

  const loadVendors = async (projectId) => {
    if (!projectId) { setVendors([]); return; }
    const r = await api.get("/vendors", { params: { project_id: projectId } });
    setVendors(r.data);
  };

  const openNew = () => { setForm(EMPTY); setVendors([]); setFilePick(null); setOpen(true); };
  const onProjectChange = (pid) => { setForm({ ...form, project_id: pid, vendor_id: "", category_id: "" }); loadVendors(pid); };

  const activeProject = projects.find(p => p.id === form.project_id);
  const enabled = (activeProject?.enabled_category_ids || []);
  const availableCats = enabled.length === 0 ? categories : categories.filter(c => enabled.includes(c.id));

  const submit = async () => {
    try {
      const payload = { ...form, amount: Number(form.amount || 0), gst_amount: Number(form.gst_amount || 0) };
      const r = await api.post("/expenses", payload);
      if (filePick) {
        try {
          const fd = new FormData(); fd.append("file", filePick);
          await api.post(`/expenses/${r.data.id}/attachments`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        } catch(e) { toast.warning("Expense saved but bill upload failed: " + (e?.response?.data?.detail || e.message)); }
      }
      toast.success("Expense recorded");
      setOpen(false); setForm(EMPTY); setFilePick(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const submitPay = async () => {
    try {
      await api.post("/expense-payments", { expense_id: payFor.id, ...pay, amount: Number(pay.amount) });
      toast.success("Payment recorded"); setPayOpen(false); setPay(PAY_EMPTY); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div>
      <PageHeader
        title="Expenses"
        subtitle="Project-wise vendor bills with partial-payment support, bill attachments and GST breakdown."
        actions={
          <div className="flex gap-2 items-center">
            <div className="relative">
              <Search className="h-4 w-4 absolute left-2.5 top-2.5 text-muted-foreground pointer-events-none"/>
              <Input data-testid="exp-search" placeholder="Search vendor / bill no / notes"
                value={q} onChange={(e)=>setQ(e.target.value)} className="pl-8 w-72"/>
            </div>
            <Select value={projectFilter} onValueChange={setProjectFilter}>
              <SelectTrigger data-testid="exp-project-filter" className="w-44"><SelectValue/></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All projects</SelectItem>
                {projects.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger data-testid="exp-status-filter" className="w-36"><SelectValue/></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                <SelectItem value="pending_approval">Awaiting approval</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="partial">Partial</SelectItem>
                <SelectItem value="paid">Paid</SelectItem>
                <SelectItem value="rejected">Rejected</SelectItem>
              </SelectContent>
            </Select>
            <Button data-testid="new-expense-btn" className="fab-m" onClick={openNew}><Plus className="h-4 w-4 mr-2"/>New expense</Button>
          </div>
        }
      />

      <div className="border border-border rounded-md bg-card overflow-hidden">
        <table className="w-full text-sm m-cards">
          <thead className="bg-muted/60"><tr className="text-left">
            <th className="px-4 py-3 font-heading text-xs uppercase tracking-wider">Date</th>
            <th className="px-4 py-3 font-heading text-xs uppercase tracking-wider">Vendor</th>
            <th className="px-4 py-3 font-heading text-xs uppercase tracking-wider">Project</th>
            <th className="px-4 py-3 font-heading text-xs uppercase tracking-wider">Category</th>
            <th className="px-4 py-3 font-heading text-xs uppercase tracking-wider">Bill #</th>
            <th className="px-4 py-3 font-heading text-xs uppercase tracking-wider text-right">Amount</th>
            <th className="px-4 py-3 font-heading text-xs uppercase tracking-wider text-right">Paid</th>
            <th className="px-4 py-3 font-heading text-xs uppercase tracking-wider text-right">Balance</th>
            <th className="px-4 py-3 font-heading text-xs uppercase tracking-wider">Status</th>
            <th className="px-4 py-3"></th>
          </tr></thead>
          <tbody>
            {list.map(e => (
              <tr key={e.id} data-testid={`exp-row-${e.id}`} className="border-t border-border hover:bg-muted/40">
                <td className="px-4 py-3 text-muted-foreground tabular">{fmtDate(e.bill_date)}</td>
                <td className="px-4 py-3">{e.vendor_name}{e.payee_type === "employee" && <span className="ml-1.5 text-[10px] uppercase tracking-widest rounded bg-muted px-1.5 py-0.5 text-muted-foreground">Staff</span>}</td>
                <td className="px-4 py-3 text-muted-foreground">{e.project_name}</td>
                <td className="px-4 py-3 text-muted-foreground">{e.category_name}</td>
                <td className="px-4 py-3 tabular">{e.bill_no || "-"} {e.attachment_ids?.length ? <Paperclip className="h-3 w-3 inline opacity-60 ml-1"/> : null}</td>
                <td className="px-4 py-3 text-right tabular">{fmtINR(e.total_amount)}</td>
                <td className="px-4 py-3 text-right tabular text-primary">{fmtINR(e.amount_paid)}</td>
                <td className="px-4 py-3 text-right tabular text-destructive">{fmtINR(e.balance)}</td>
                <td className="px-4 py-3"><StatusBadge status={e.status}/></td>
                <td className="px-4 py-3 text-right">
                  {e.status === "pending_approval" && (
                    <div className="flex justify-end gap-1">
                      <Button data-testid={`approve-${e.id}`} size="sm" variant="outline"
                        onClick={async ()=>{ try { await api.post(`/expenses/${e.id}/decide`, { decision: "approve" }); toast.success("Approved"); load(); } catch(err){ toast.error(err?.response?.data?.detail || "Failed"); } }}>Approve</Button>
                      <Button data-testid={`reject-${e.id}`} size="sm" variant="ghost" className="text-red-700"
                        onClick={async ()=>{ const reason = window.prompt("Reason for rejection?"); if (reason === null) return; try { await api.post(`/expenses/${e.id}/decide`, { decision: "reject", reason }); toast.info("Rejected"); load(); } catch(err){ toast.error(err?.response?.data?.detail || "Failed"); } }}>Reject</Button>
                    </div>
                  )}
                  {e.status !== "paid" && e.status !== "pending_approval" && e.status !== "rejected" && (
                    <Button data-testid={`pay-exp-${e.id}`} size="sm" variant="outline"
                      onClick={()=>{ setPayFor(e); setPay({...PAY_EMPTY, amount: e.balance}); setPayOpen(true); }}>
                      <Wallet className="h-3 w-3 mr-1"/>Pay
                    </Button>
                  )}
                </td>
              </tr>
            ))}
            {list.length === 0 && <tr><td colSpan={10} className="px-4 py-10 text-center text-muted-foreground">No expenses yet.</td></tr>}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>New expense</DialogTitle></DialogHeader>
          <div className="grid gap-3 grid-cols-2 py-2">
            <div className="col-span-2">
              <Label>Project</Label>
              <Select value={form.project_id} onValueChange={onProjectChange}>
                <SelectTrigger data-testid="exp-project"><SelectValue placeholder="Select project"/></SelectTrigger>
                <SelectContent>{projects.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Vendor</Label>
              <Select value={form.vendor_id} onValueChange={(v)=>{ if (v === "__new__") setQvOpen(true); else setForm({...form,vendor_id:v}); }} disabled={!form.project_id}>
                <SelectTrigger data-testid="exp-vendor"><SelectValue placeholder={form.project_id ? "Select vendor" : "Pick project first"}/></SelectTrigger>
                <SelectContent>
                  {vendors.map(v => <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>)}
                  <SelectItem data-testid="exp-vendor-add-new" value="__new__" className="text-primary font-medium border-t border-border mt-1">
                    <span className="inline-flex items-center gap-1.5"><Plus className="h-3.5 w-3.5"/>Add new vendor</span>
                  </SelectItem>
                </SelectContent>
              </Select>
              <QuickVendorDialog open={qvOpen} onOpenChange={setQvOpen} projectId={form.project_id}
                projectName={projects.find((p) => p.id === form.project_id)?.name}
                onCreated={(v) => {
                  setVendors((vs) => [...vs, v].sort((a, b) => a.name.localeCompare(b.name)));
                  setForm((f) => ({ ...f, vendor_id: v.id }));
                }}/>
            </div>
            <div>
              <Label>Category</Label>
              <Select value={form.category_id} onValueChange={(v)=>setForm({...form,category_id:v})} disabled={!form.project_id}>
                <SelectTrigger data-testid="exp-category"><SelectValue placeholder={form.project_id ? (availableCats.length ? "Select category" : "Enable categories in project first") : "Pick project first"}/></SelectTrigger>
                <SelectContent>{availableCats.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Bill date</Label><Input data-testid="exp-bill-date" type="date" value={form.bill_date} onChange={(e)=>setForm({...form,bill_date:e.target.value})}/></div>
            <div><Label>Due date (optional)</Label><Input data-testid="exp-due-date" type="date" value={form.due_date} onChange={(e)=>setForm({...form,due_date:e.target.value})}/></div>
            <div><Label>Amount (₹)</Label><Input data-testid="exp-amount" type="number" step="0.01" value={form.amount} onChange={(e)=>setForm({...form,amount:e.target.value})}/></div>
            <div><Label>GST (₹, optional)</Label><Input data-testid="exp-gst" type="number" step="0.01" value={form.gst_amount} onChange={(e)=>setForm({...form,gst_amount:e.target.value})}/></div>
            <div><Label>Bill number</Label><Input data-testid="exp-bill-no" value={form.bill_no} onChange={(e)=>setForm({...form,bill_no:e.target.value})}/></div>
            <div><Label>Bill attachment</Label><Input data-testid="exp-file" type="file" accept="image/png,image/jpeg,image/webp,application/pdf" onChange={(e)=>setFilePick(e.target.files?.[0] || null)}/></div>
            <div className="col-span-2"><Label>Notes</Label><Input data-testid="exp-notes" value={form.notes} onChange={(e)=>setForm({...form,notes:e.target.value})}/></div>
          </div>
          <DialogFooter><Button data-testid="exp-save-btn" onClick={submit}>Record expense</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={payOpen} onOpenChange={setPayOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Pay vendor · {payFor?.vendor_name}</DialogTitle></DialogHeader>
          <div className="grid gap-3 py-2">
            <div className="text-sm text-muted-foreground">Balance due: <span className="tabular font-medium text-foreground">{fmtINR(payFor?.balance || 0)}</span></div>
            <div><Label>Amount</Label><Input data-testid="exp-pay-amount" type="number" step="0.01" value={pay.amount} onChange={(e)=>setPay({...pay,amount:e.target.value})}/></div>
            <div>
              <Label>Method</Label>
              <Select value={pay.method} onValueChange={(v)=>setPay({...pay,method:v})}>
                <SelectTrigger data-testid="exp-pay-method"><SelectValue/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="cash">Cash</SelectItem>
                  <SelectItem value="cheque">Cheque</SelectItem>
                  <SelectItem value="bank_transfer">Bank transfer</SelectItem>
                  <SelectItem value="upi">UPI</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label>Reference / UTR</Label><Input data-testid="exp-pay-ref" value={pay.reference_no} onChange={(e)=>setPay({...pay,reference_no:e.target.value})}/></div>
            <div><Label>Paid date</Label><Input data-testid="exp-pay-date" type="date" value={pay.paid_date} onChange={(e)=>setPay({...pay,paid_date:e.target.value})}/></div>
            <div><Label>Notes</Label><Input data-testid="exp-pay-notes" value={pay.notes} onChange={(e)=>setPay({...pay,notes:e.target.value})}/></div>
          </div>
          <DialogFooter><Button data-testid="exp-pay-submit" onClick={submitPay}>Record payment</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
